import React, { useCallback, useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { useMutation, useQuery } from '@tanstack/react-query'
import { HorizontalFormSection } from '../../common/HorizontalFormSection'
import { IconChoices } from '../../common/Icon'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import {
  isUserAdminInSociety,
  isUserBoardMemberInSociety,
} from '../../../helpers/society'
import { userNotificationsSchema } from '../../../forms/schemas/user_notifications'
import { ToastType } from '../../common/Toast/toast-type'
import { theme } from '../../../theme/theme'
import { DropdownSelect } from '../../common/DropdownSelect'
import { useSocietyDropdownOptions } from './useSocietyDropdownOptions'
import { NotificationSettingsRow } from './NotificationSettingsRow'
import { useNotificationSettingsHelpers } from './helpers'
import { SocietyErrorViewWrapper } from '../../../views/error/SocietyErrorViewWrapper'
import { useAuth } from '../../../hooks/useAuth'
import { societyQueries } from '../../../api/societies'
import { patchUser, userQueries } from '../../../api/users'
import { i18nSocietyType } from '../../../i18n'

export const NotificationSettings = (): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { userId: authUserId } = useAuth()
  const userId = authUserId as string
  const { data: societies, isSuccess: hasLoadedSocieties } = useQuery(
    societyQueries.getUserSocieties(userId)
  )
  const societyOptions = useSocietyDropdownOptions()
  const [societyId, setSocietyId] = useState<undefined | string>(undefined)

  useEffect(() => {
    if (hasLoadedSocieties && societyId === undefined) {
      setSocietyId(societyOptions[0]?.value)
    }
  }, [hasLoadedSocieties, societyId, societyOptions])

  const society = societyId
    ? societies?.find((_society) => _society._id === societyId)
    : undefined

  const { data: user } = useQuery(userQueries.getUser(userId))

  const {
    createPatchFormData,
    getDefaultFormValues,
    getOnMasterSwitchChangeResetValues,
  } = useNotificationSettingsHelpers(societyId, user)

  const isAdmin = isUserAdminInSociety(society, userId)
  const isBoardMember = isUserBoardMemberInSociety(society, userId)

  const getDefaultValues = useCallback((): InferType<
    typeof userNotificationsSchema
  > => {
    return getDefaultFormValues()
  }, [getDefaultFormValues])

  const { control, reset, watch } = useForm({
    resolver: yupResolver(userNotificationsSchema),
    defaultValues: getDefaultValues(),
  })

  useEffect(() => {
    reset(getDefaultValues())
  }, [reset, getDefaultValues])

  const { mutate: mutatePatchData, isPending: isUpdatingUser } = useMutation({
    mutationFn: patchUser,

    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('flashMessage.changesSaved')
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('flashMessage.changesNotSavedError')
      )
    },
  })
  useEffect(() => {
    const subscription = watch(async (data) => {
      if (!user) {
        return
      }
      const formData = createPatchFormData(
        data as InferType<typeof userNotificationsSchema>
      )
      mutatePatchData({ id: userId, body: formData })
    })
    return () => subscription.unsubscribe()
  }, [createPatchFormData, mutatePatchData, user, userId, watch])

  const onMasterSwitchChange = (
    field: 'email' | 'mobile',
    value: boolean | undefined
  ): void => {
    reset(getOnMasterSwitchChangeResetValues(getDefaultValues(), field, value))
  }

  return (
    <SocietyErrorViewWrapper>
      <form>
        <div className="mt-4 mb-8 md:mb-2">
          <p className="mb-2" style={theme.textVariants.captionBold}>
            {translate('common.form.labels.society', {
              context: i18nSocietyType(society?.societyTypeDisplayName),
            })}
          </p>
          <DropdownSelect
            value={societyId}
            options={societyOptions}
            onChange={(value) => setSocietyId(value)}
            dropdownWidth="w-full md:w-60"
            dropdownContentWidth="w-fit"
            dropdownPosition="bottom-right"
            dropdownClassName="w-min-full"
          />
        </div>
        <HorizontalFormSection
          title={translate('notificationSettings.sections.all.title')}
          icon={IconChoices.NOTIFICATION_ALERT}
          iconPlacement="left"
          borderBottom
        >
          <Controller
            control={control}
            render={({ field: { value, name } }) => (
              <NotificationSettingsRow
                label={translate(
                  'notificationSettings.sections.all.allNotificationsTitle'
                )}
                isUpdatingUser={isUpdatingUser}
                name={name}
                value={value}
                onChange={(_value: {
                  mobile?: boolean | undefined
                  email?: boolean | undefined
                }) => {
                  const field =
                    value.email !== _value.email ? 'email' : 'mobile'
                  onMasterSwitchChange(field, _value[field])
                }}
              />
            )}
            name="masterSwitch"
          />
        </HorizontalFormSection>
        {isAdmin && (
          <HorizontalFormSection
            title={translate('notificationSettings.sections.admin.title')}
            icon={IconChoices.ADMIN}
            iconPlacement="left"
            borderBottom
          >
            <Controller
              control={control}
              render={({ field: { value, name, onChange } }) => (
                <NotificationSettingsRow
                  label={translate(
                    'notificationSettings.sections.admin.membershipRequestTitle'
                  )}
                  isUpdatingUser={isUpdatingUser}
                  name={name}
                  value={value}
                  onChange={onChange}
                />
              )}
              name="general.membershipRequests"
            />
          </HorizontalFormSection>
        )}
        <HorizontalFormSection
          title={translate('notificationSettings.sections.feed.title', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          })}
          icon={IconChoices.FEED}
          iconPlacement="left"
          borderBottom
        >
          <Controller
            control={control}
            render={({ field: { value, name, onChange } }) => (
              <NotificationSettingsRow
                label={translate(
                  'notificationSettings.sections.feed.boardNewPostTitle',
                  { context: i18nSocietyType(society?.societyTypeDisplayName) }
                )}
                isUpdatingUser={isUpdatingUser}
                name={name}
                value={value}
                onChange={onChange}
                divider
                disableMobile
              />
            )}
            name="feed.postFromAdmins"
          />
          <Controller
            control={control}
            render={({ field: { value, name, onChange } }) => (
              <NotificationSettingsRow
                label={translate(
                  'notificationSettings.sections.feed.boardNewCommentTitle',
                  { context: i18nSocietyType(society?.societyTypeDisplayName) }
                )}
                isUpdatingUser={isUpdatingUser}
                name={name}
                value={value}
                onChange={onChange}
                divider
              />
            )}
            name="feed.commentOnAdminPosts"
          />
          <Controller
            control={control}
            render={({ field: { value, name, onChange } }) => (
              <NotificationSettingsRow
                label={translate(
                  'notificationSettings.sections.feed.neighbourNewPostTitle'
                )}
                isUpdatingUser={isUpdatingUser}
                name={name}
                value={value}
                onChange={onChange}
                divider
              />
            )}
            name="feed.postFromNeighbours"
          />
          <Controller
            control={control}
            render={({ field: { value, name, onChange } }) => (
              <NotificationSettingsRow
                label={translate(
                  'notificationSettings.sections.feed.neighbourNewCommentTitle'
                )}
                isUpdatingUser={isUpdatingUser}
                name={name}
                value={value}
                onChange={onChange}
              />
            )}
            name="feed.commentOnNeighbourPosts"
          />
        </HorizontalFormSection>
        <HorizontalFormSection
          title={translate('notificationSettings.sections.chat.title')}
          icon={IconChoices.CHAT}
          iconPlacement="left"
        >
          <Controller
            control={control}
            render={({ field: { value, name, onChange } }) => (
              <NotificationSettingsRow
                label={translate(
                  'notificationSettings.sections.chat.neighbourNewMessageTitle'
                )}
                isUpdatingUser={isUpdatingUser}
                name={name}
                value={value}
                onChange={onChange}
                divider
              />
            )}
            name="chat.neighbours"
          />
          <Controller
            control={control}
            render={({ field: { value, name, onChange } }) => (
              <NotificationSettingsRow
                label={translate(
                  'notificationSettings.sections.chat.groupNewMessageTitle'
                )}
                isUpdatingUser={isUpdatingUser}
                name={name}
                value={value}
                onChange={onChange}
                divider
              />
            )}
            name="chat.groups"
          />
          {(isAdmin || isBoardMember) && (
            <>
              <Controller
                control={control}
                render={({ field: { value, name, onChange } }) => (
                  <NotificationSettingsRow
                    label={translate(
                      'notificationSettings.sections.chat.boardNewMessageTitle',
                      {
                        context: i18nSocietyType(
                          society?.societyTypeDisplayName
                        ),
                      }
                    )}
                    isUpdatingUser={isUpdatingUser}
                    name={name}
                    value={value}
                    onChange={onChange}
                    divider
                  />
                )}
                name="chat.board"
              />
            </>
          )}
        </HorizontalFormSection>

        <HorizontalFormSection
          title={translate('notificationSettings.sections.ticket.title')}
          icon={IconChoices.ASSIGNMENT}
          iconPlacement="left"
        >
          <p style={theme.textVariants.base}>
            {translate(
              'notificationSettings.sections.ticket.forMeAsResidentTitle'
            )}
          </p>
          <Controller
            control={control}
            render={({ field: { value, name, onChange } }) => (
              <NotificationSettingsRow
                label={translate(
                  'notificationSettings.sections.ticket.newTicketFromBoard'
                )}
                isUpdatingUser={isUpdatingUser}
                name={name}
                value={value}
                onChange={onChange}
                divider
              />
            )}
            name="ticket.newTicketFromBoard"
          />
          <Controller
            control={control}
            render={({ field: { value, name, onChange } }) => (
              <NotificationSettingsRow
                label={translate(
                  'notificationSettings.sections.ticket.newMessageFromBoard'
                )}
                isUpdatingUser={isUpdatingUser}
                name={name}
                value={value}
                onChange={onChange}
                divider
              />
            )}
            name="ticket.newMessageFromBoard"
          />

          {(isAdmin || isBoardMember) && (
            <>
              <p style={theme.textVariants.base}>
                {translate(
                  'notificationSettings.sections.ticket.forMeInBoardTitle'
                )}
              </p>
              <Controller
                control={control}
                render={({ field: { value, name, onChange } }) => (
                  <NotificationSettingsRow
                    label={translate(
                      'notificationSettings.sections.ticket.newTicket'
                    )}
                    isUpdatingUser={isUpdatingUser}
                    name={name}
                    value={value}
                    onChange={onChange}
                    divider
                  />
                )}
                name="ticket.newTicket"
              />

              <Controller
                control={control}
                render={({ field: { value, name, onChange } }) => (
                  <NotificationSettingsRow
                    label={translate(
                      'notificationSettings.sections.ticket.newMessage'
                    )}
                    isUpdatingUser={isUpdatingUser}
                    name={name}
                    value={value}
                    onChange={onChange}
                    divider
                  />
                )}
                name="ticket.newMessage"
              />
            </>
          )}
        </HorizontalFormSection>
      </form>
    </SocietyErrorViewWrapper>
  )
}
